import { Dialog } from '../../../shared/ui/dialog';
import {
  useGetMailAttachments,
  useParsedEmailDetails
} from '../../../features/parsed-emails/parsed-emails-actions/parsed-emails-actions.hook';
import { Stack, Box, Divider, Typography } from '@mui/material';
import { LinkToFile } from '../../../shared/ui/links/link-to-file';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { formatDateTime } from 'utils/date-format-helpers';
import EmailAddress from '../../../shared/ui/email-address';
import { LoadingBanner } from '../../../shared/ui/loading-banner';
import { ErrorBanner } from '../../../shared/ui/error-banner';
import { useEffect, useRef } from 'react';

type ViewEmailModalProps = {
  open: boolean;
  onClose: () => void;
  id: number;
  leadId?: number;
}

export const ViewEmailModal = ({ open, onClose, id, leadId }: ViewEmailModalProps) => {
  const emailBodyRef = useRef<HTMLDivElement>(null);
  const { parsedEmailData, isLoading: isEmailLoading, error: emailError } = useParsedEmailDetails(id, leadId);
  const attachmentIds = parsedEmailData?.attachmentIds
    ? parsedEmailData.attachmentIds.split(',').map(Number).filter((fileId) => Number.isInteger(fileId) && fileId > 0)
    : [];
  const { attachments, isLoading: areAttachmentsLoading, error: attachmentsError } = useGetMailAttachments(attachmentIds, attachmentIds.length === 0, leadId);

  useEffect(() => {
    emailBodyRef.current?.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
      if (link.protocol !== 'http:' && link.protocol !== 'https:') return;

      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    });
  }, [open, parsedEmailData?.body]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <Dialog.Title>{parsedEmailData?.subject || 'Письмо'}</Dialog.Title>
      <Dialog.CloseButton onClick={onClose} />
      <Dialog.Content>
        {isEmailLoading && <LoadingBanner />}
        {emailError && <ErrorBanner errorMessage={emailError} />}
        {parsedEmailData && (
          <Stack spacing={2}>
            <Stack direction="column" spacing={0.75} sx={{ backgroundColor: '#f4f4f4', borderRadius: '10px' }} p={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>От:</Typography>
                <EmailAddress email={parsedEmailData.from}/>
                {parsedEmailData.type === 'OUT' ? <ArrowCircleDownIcon color="success"/> : <ArrowCircleUpIcon color="warning"/>}
              </Stack>
              <Box display="flex" gap={0.5}>Кому: <EmailAddress email={parsedEmailData.to}/></Box>
              {parsedEmailData.cc && <Box display="flex" gap={0.5}>Копия: <EmailAddress email={parsedEmailData.cc}/></Box>}
              {parsedEmailData.bcc && <Box display="flex" gap={0.5}>Скрытая копия: <EmailAddress email={parsedEmailData.bcc}/></Box>}
              <Typography variant="caption">Дата получения: {formatDateTime(parsedEmailData.date)}</Typography>
            </Stack>

            <Box
              ref={emailBodyRef}
              sx={{
                minHeight: 80,
                overflowWrap: 'anywhere',
                userSelect: 'text',
                cursor: 'text',
                '& *': { userSelect: 'text !important' },
                '& img': { maxWidth: '100%', height: 'auto' },
              }}
              dangerouslySetInnerHTML={{ __html: parsedEmailData.body }}
            />

            <Divider />
            <Box>
              <Typography variant="subtitle2" mb={1}>Вложения</Typography>
              {areAttachmentsLoading && <LoadingBanner />}
              {attachmentsError && <ErrorBanner errorMessage={attachmentsError} />}
              {!areAttachmentsLoading && !attachmentsError && (
                attachments?.length ? (
                  <Box display="flex" flexWrap="wrap" gap={2} sx={{ '& > a': { display: 'flex' } }}>
                    {attachments.map((file) => (
                      <LinkToFile
                        key={file.id}
                        file={{ id: file.id, fileName: file.fileName, size: file.size }}
                        icon={<FilePresentIcon/>}
                        downloadUrl={leadId ? `/api/lead/${leadId}/email/attachments/${file.id}` : undefined}
                      />
                    ))}
                  </Box>
                ) : <Typography color="text.secondary">Нет вложений</Typography>
              )}
            </Box>
          </Stack>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
